/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Input,
  Stack,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import {
  Controller,
  Control,
  FieldErrors,
  UseFormSetValue,
  UseFormSetError,
  UseFormClearErrors,
  useWatch,
} from 'react-hook-form';
import Asterisk from '@/components/common/Asterisk';
import { useGetBrands, useGetFabrics, useGetCollections } from '@/services/addProduct';
import VideoUploadSection from './VideoUploadSection';
import { type FormData } from '@/services/addProduct/types';
import CustomSelect from '@/components/common/CustomSelect';
import ImageUploaderField from '../common/ImageUploaderField';
import { useMemo } from 'react';
import { IProductEditResponse } from '@/services/manageProduct/types';

// Define proper types
interface Brand {
  id: string | number;
  name: string;
}

interface Fabric {
  id: string | number;
  name: string;
}

interface Collection {
  id: string | number;
  name: string;
}

interface CollectionsResponse {
  results: Collection[];
}

interface ProductDetailsSectionProps {
  control: Control<FormData>;
  errors: FieldErrors<FormData>;
  product?: IProductEditResponse;
  setValue: UseFormSetValue<FormData>;
  clearErrors: UseFormClearErrors<FormData>;
  setError: UseFormSetError<FormData>;
  categories?: any[];
  defaultCategoryImage?: string;
}

// Define Option type to match CustomSelect
interface Option {
  id: string;
  label: string;
  searchLabel?: string; // For searchable dropdown
}

const ProductDetailsSection = ({
  control,
  errors,
  product,
  setValue,
  clearErrors,
  setError,
  categories,
}: ProductDetailsSectionProps) => {
  const { data: brands } = useGetBrands();
  const { data: fabrics } = useGetFabrics();
  const { data: collections } = useGetCollections();

  const selectedCategoryId = useWatch({
    control,
    name: 'categorySelection.category',
  });

  const selectedCategoryName = useMemo(() => {
    if (!selectedCategoryId || !categories) return undefined;
    const cat = categories.find(
      (c: { id: string | number }) => c.id.toString() === selectedCategoryId.toString(),
    );
    return cat?.name;
  }, [selectedCategoryId, categories]);

  const watchedPreviewImage = useWatch({
    control,
    name: 'productDetails.previewImage',
  });

  // Map brands to CustomSelect options format
  const brandOptions: Option[] = [
    ...(brands?.filter((brand: Brand) => brand.name.toLowerCase() === 'no brand') || []),
    ...(brands?.filter((brand: Brand) => brand.name.toLowerCase() !== 'no brand') || []),
  ].map((brand: Brand) => ({
    id: brand.id.toString(),
    label: brand.name,
    searchLabel: brand.name, // Enable search
  }));

  // Map fabrics to CustomSelect options format with "No Fabric" moved to top if it exists
  const fabricOptions: Option[] = [
    ...(fabrics?.filter((fabric: Fabric) => fabric.name.toLowerCase() === 'no fabric') || []),
    ...(fabrics?.filter((fabric: Fabric) => fabric.name.toLowerCase() !== 'no fabric') || []),
  ].map((fabric: Fabric) => ({
    id: fabric.id.toString(),
    label: fabric.name,
    searchLabel: fabric.name, // Enable search
  }));

  // Map collections to CustomSelect options format
  const collectionOptions: Option[] =
    (collections as CollectionsResponse)?.results?.map((col: Collection) => ({
      id: col.id.toString(),
      label: col.name,
      searchLabel: col.name, // Enable search
    })) || [];

  return (
    <Box
      border={'1px solid'}
      borderColor={'border.default60'}
      bg={'background.secondary'}
      borderRadius={'lg'}
    >
      <Flex p={4} direction="column" gap={4}>
        <Text fontSize={'md'} fontWeight={'semibold'}>
          Product Details
        </Text>
        <VStack spacing={4} align="stretch">
          {/* Name and Brand */}
          <Stack direction={{ base: 'column', md: 'row' }} spacing={4} w="100%">
            <FormControl isInvalid={!!errors.productDetails?.name}>
              <FormLabel
                fontSize={'sm'}
                color={errors.productDetails?.name ? 'status.error' : 'text.secondary'}
              >
                Product Name
                <Asterisk />
              </FormLabel>
              <Controller
                name="productDetails.name"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    rounded={'lg'}
                    placeholder="Enter Product Name"
                    bg={'background.white'}
                  />
                )}
              />
              <FormErrorMessage>{errors.productDetails?.name?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.productDetails?.brand}>
              <FormLabel
                fontSize={'sm'}
                color={errors.productDetails?.brand ? 'status.error' : 'text.secondary'}
              >
                Brand
                <Asterisk />
              </FormLabel>
              <Controller
                name="productDetails.brand"
                control={control}
                render={({ field }) => (
                  <CustomSelect
                    options={brandOptions}
                    placeholder="Select Brand"
                    selectedValue={field.value?.toString() || null}
                    onChange={(value: string) => field.onChange(value)}
                    size="md"
                    isFullWidth={true}
                    isSearchable={true}
                  />
                )}
              />
              <FormErrorMessage>{errors.productDetails?.brand?.message}</FormErrorMessage>
            </FormControl>
          </Stack>

          {/* Discount and Fabric */}
          <Stack direction={{ base: 'column', md: 'row' }} spacing={4} w="100%">
            <FormControl isInvalid={!!errors.productDetails?.discount}>
              <FormLabel fontSize={'sm'} color={'text.secondary'}>
                Discount Percentage
                <Asterisk color="#0000" />
              </FormLabel>
              <Controller
                name="productDetails.discount"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Enter Discount %"
                    rounded={'lg'}
                    bg={'background.white'}
                    onClick={() => {
                      if (field.value === 0) {
                        field.onChange('');
                      }
                    }}
                  />
                )}
              />
              <FormErrorMessage>{errors.productDetails?.discount?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.productDetails?.fabric}>
              <FormLabel
                fontSize={'sm'}
                color={errors.productDetails?.fabric ? 'status.error' : 'text.secondary'}
              >
                Fabric
                <Asterisk />
              </FormLabel>
              <Controller
                name="productDetails.fabric"
                control={control}
                render={({ field }) => (
                  <CustomSelect
                    options={fabricOptions}
                    placeholder="Select Fabric"
                    selectedValue={field.value?.toString() || null}
                    onChange={(value: string) => field.onChange(value)}
                    size="md"
                    isFullWidth={true}
                    isSearchable={true}
                  />
                )}
              />
              <FormErrorMessage>{errors.productDetails?.fabric?.message}</FormErrorMessage>
            </FormControl>
          </Stack>

          {/* Short Description and Video */}
          <Stack direction={{ base: 'column', md: 'row' }} spacing={4} w="100%">
            <FormControl isInvalid={!!errors.productDetails?.shortDescription}>
              <FormLabel
                fontSize={'sm'}
                color={errors.productDetails?.shortDescription ? 'status.error' : 'text.secondary'}
              >
                Short Description
                <Asterisk />
              </FormLabel>
              <Controller
                name="productDetails.shortDescription"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    placeholder="Enter short description"
                    rounded={'lg'}
                    bg={'background.white'}
                  />
                )}
              />
              <FormErrorMessage>
                {errors.productDetails?.shortDescription?.message}
              </FormErrorMessage>
            </FormControl>

            <VideoUploadSection
              control={control}
              errors={errors}
              product={product}
              setValue={setValue}
              clearErrors={clearErrors}
              setError={setError}
            />
          </Stack>

          {/* Product Specifications and Detailed Description */}
          <Stack direction={{ base: 'column', md: 'row' }} spacing={4} w="100%">
            <FormControl isInvalid={!!errors.productDetails?.productSpecifications}>
              <FormLabel fontSize={'sm'} color={'text.secondary'}>
                Product Specifications
              </FormLabel>
              <Controller
                name="productDetails.productSpecifications"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    placeholder="Enter specifications"
                    rounded={'lg'}
                    bg={'background.white'}
                  />
                )}
              />
              <FormErrorMessage>
                {errors.productDetails?.productSpecifications?.message}
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.productDetails?.detailedProductDescription}>
              <FormLabel fontSize={'sm'} color={'text.secondary'}>
                Detailed Product Description
              </FormLabel>
              <Controller
                name="productDetails.detailedProductDescription"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    placeholder="A brief description of the product"
                    rounded={'lg'}
                    bg={'background.white'}
                  />
                )}
              />
              <FormErrorMessage>
                {errors.productDetails?.detailedProductDescription?.message}
              </FormErrorMessage>
            </FormControl>
          </Stack>

          {/* Additional Info and Collection */}
          <Grid templateColumns={{ base: 'repeat(1,1fr)', lg: 'repeat(2,1fr)' }} gap={4}>
            <FormControl isInvalid={!!errors.productDetails?.additionalInformation}>
              <FormLabel fontSize={'sm'} color={'text.secondary'}>
                Additional Information (Optional)
              </FormLabel>
              <Controller
                name="productDetails.additionalInformation"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    placeholder="Enter additional info"
                    rounded={'lg'}
                    bg={'background.white'}
                  />
                )}
              />
              <FormErrorMessage>
                {errors.productDetails?.additionalInformation?.message}
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.productDetails?.collection}>
              <FormLabel
                fontSize={'sm'}
                color={errors.productDetails?.collection ? 'status.error' : 'text.secondary'}
              >
                Collection
              </FormLabel>
              <Controller
                name="productDetails.collection"
                control={control}
                render={({ field }) => (
                  <CustomSelect
                    options={collectionOptions}
                    placeholder="Select Collection"
                    selectedValue={field.value?.toString() || null}
                    onChange={(value: string) => field.onChange(value)}
                    size="md"
                    isFullWidth={true}
                    isSearchable={true}
                  />
                )}
              />
              <FormErrorMessage>{errors.productDetails?.collection?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.productDetails?.stitchTime}>
              <FormLabel fontSize={'sm'} color={'text.secondary'}>
                Stitch Time(in days) <Asterisk />
                <Asterisk color="#0000" />
              </FormLabel>
              <Controller
                name="productDetails.stitchTime"
                control={control}
                defaultValue={2}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Enter stitch time"
                    rounded={'lg'}
                    bg={'background.white'}
                    value={field.value ?? 2}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^[0-9]+$/.test(value)) {
                        field.onChange(value);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                )}
              />
              <FormErrorMessage>{errors.productDetails?.stitchTime?.message}</FormErrorMessage>
            </FormControl>
            {!selectedCategoryName?.toLowerCase().includes('saree') && (
              <FormControl>
                <FormLabel fontSize="sm" color="text.secondary">
                  Size Guide Image
                </FormLabel>
                <ImageUploaderField
                  name="productDetails.previewImage"
                  control={control}
                  categoryId={selectedCategoryId}
                  currentValue={watchedPreviewImage}
                />
              </FormControl>
            )}
          </Grid>
        </VStack>
      </Flex>
    </Box>
  );
};

export default ProductDetailsSection;
